import { Component } from '@theme/component';

/**
 * @typedef {Object} TabsRefs
 * @property {HTMLElement[]} tabs
 * @property {HTMLElement[]} panels
 * @property {HTMLElement} tablist
 */

/** @extends {Component<TabsRefs>} */
export class TabsComponent extends Component {
  requiredRefs = ['tabs', 'panels', 'tablist'];
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs, tablist } = this.refs;
    if (!tabs?.length) return;

    // Trigger buttons render next to their panel (block settings only resolve inside
    // the block's own template), so move them into the tablist here.
    for (const tab of tabs) {
      tablist.appendChild(tab);
    }

    this.#active = Math.max(
      0,
      tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')
    );

    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };

    for (const [index, tab] of tabs.entries()) {
      tab.addEventListener('click', () => this.#activate(index), opts);
      tab.addEventListener('keydown', (event) => this.#handleKeydown(event, index), opts);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  /**
   * @param {KeyboardEvent} event
   * @param {number} index
   */
  #handleKeydown(event, index) {
    const { tabs } = this.refs;
    if (!tabs) return;

    let offset;
    switch (event.key) {
      case 'ArrowLeft':
        offset = -1;
        break;
      case 'ArrowRight':
        offset = 1;
        break;
      case 'Home':
        offset = -index;
        break;
      case 'End':
        offset = tabs.length - 1 - index;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextIndex = (index + offset + tabs.length) % tabs.length;
    tabs[nextIndex]?.focus();
    this.#activate(nextIndex);
  }

  /** @param {number} index */
  #activate(index) {
    const { tabs, panels } = this.refs;
    if (!tabs || !panels || index === this.#active || index < 0 || index >= tabs.length) return;

    this.#active = index;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === index;
      tab.setAttribute('aria-selected', String(isActive));
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    }

    for (const [i, panel] of panels.entries()) {
      panel.toggleAttribute('inert', i !== index);
    }
  }
}

customElements.define('tabs-component', TabsComponent);
